import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  LayoutDashboard, Users, Receipt, Settings, LogOut, Plus,
  Check, Eye, EyeOff, ChevronRight, Upload, RefreshCw,
  UserCheck, UserX, Building2, Shield, Pencil, KeyRound,
  ToggleLeft, ToggleRight, SlidersHorizontal, X, AlertCircle,
  Download, Trash2, FileText, Sun, Moon, TicketCheck, ChevronDown
} from 'lucide-react';
import { getAllDeletionRequests, resolveDeletionRequest } from '../../api/admin.api';
import { getAllTickets, updateTicketStatus } from '../../api/ticket.api';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import {
  getDashboardStats, getAllUsers, createUser, updateUser, updateUserSettings,
  resetUserPassword, toggleUserActive, updateUserControl,
  generateInvoice, getAllBilling, updateInvoiceStatus,
} from '../../api/admin.api';
import { impersonate } from '../../api/auth.api';
import Logo, { setLogoUrl, getLogoUrl } from '../../components/common/Logo';
import StatCard from '../../components/common/StatCard';
import Button   from '../../components/common/Button';
import Input    from '../../components/common/Input';
import Modal    from '../../components/common/Modal';
import './AdminDashboard.css';
import CompanySettings from './CompanySettings';
import { generateInvoicePDF } from '../../utils/invoicePDF';

const MODULES = [
  { key: 'year_end_accounting', label: 'Year End Accounting',  price: '₹500 / society'       },
  { key: 'society_book',        label: 'Society Book',         price: '₹20 / member / month' },
  { key: 'income_expenditure',  label: 'Income & Expenditure', price: '₹500 / society'       },
];

const TABS = [
  { key: 'dashboard', label: 'Dashboard',  Icon: LayoutDashboard },
  { key: 'users',     label: 'Users',      Icon: Users            },
  { key: 'societies', label: 'Societies',  Icon: Building2        },
  { key: 'billing',   label: 'Billing',    Icon: Receipt          },
  { key: 'tickets',   label: 'Tickets',    Icon: TicketCheck      },
  { key: 'settings',  label: 'Settings',   Icon: Settings         },
];

const TICKET_STATUSES = ['Pending', 'In Progress', 'Resolved', 'Closed'];

const ticketStatusBadge = (s) => ({
  'Pending':     'badge-warning',
  'In Progress': 'badge-info',
  'Resolved':    'badge-success',
  'Closed':      'badge-subtle',
}[s] || 'badge-subtle');

// ── User Filter Panel ─────────────────────────────────────────────────────────
const UserFilterPanel = ({ users, filter, onClose }) => {
  const filtered = users.filter(u =>
    filter === 'active'   ? u.isActive  :
    filter === 'inactive' ? !u.isActive : true
  );
  const title = filter === 'active' ? 'Active Users' : filter === 'inactive' ? 'Inactive Users' : 'All Users';
  return (
    <div style={{ position:'fixed', inset:0, zIndex:300, display:'flex' }}>
      <div style={{ flex:1, background:'rgba(0,0,0,0.5)' }} onClick={onClose} />
      <div style={{ width:420, background:'var(--bg-surface)', borderLeft:'1px solid var(--border-subtle)', padding:'1.5rem', overflowY:'auto', animation:'slideInRight 0.22s ease' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1.5rem' }}>
          <h3>{title} ({filtered.length})</h3>
          <Button variant="ghost" size="sm" icon={X} onClick={onClose} />
        </div>
        <div style={{ display:'flex', flexDirection:'column', gap:'0.6rem' }}>
          {filtered.map(u => (
            <div key={u._id} style={{ padding:'0.85rem 1rem', background:'var(--bg-raised)', borderRadius:'var(--radius-sm)', border:'1px solid var(--border-subtle)', display:'flex', alignItems:'center', gap:'0.75rem' }}>
              <div className="user-av">{u.name[0]}</div>
              <div style={{ flex:1 }}>
                <div className="user-cell-name">{u.name}</div>
                <div className="uid-tag" style={{ display:'inline-block', marginTop:'0.2rem' }}>{u.userId}</div>
              </div>
              <span className={`badge ${u.isActive ? 'badge-success' : 'badge-danger'}`}>{u.isActive ? 'Active' : 'Inactive'}</span>
            </div>
          ))}
        </div>
      </div>
      <style>{`@keyframes slideInRight { from { transform:translateX(100%); } to { transform:translateX(0); } }`}</style>
    </div>
  );
};

// ── Tickets Tab ───────────────────────────────────────────────────────────────
const TicketsTab = ({ onRefresh }) => {
  const [tickets,       setTickets]       = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [filterStatus,  setFilterStatus]  = useState('');
  const [expandedId,    setExpandedId]    = useState(null);
  const [updatingId,    setUpdatingId]    = useState(null);
  const [statusForm,    setStatusForm]    = useState({ status: '', adminNote: '' });
  const [editingId,     setEditingId]     = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getAllTickets(filterStatus || undefined);
      setTickets(r.data.data || []);
    } catch { toast.error('Failed to load tickets'); }
    finally { setLoading(false); }
  }, [filterStatus]);

  useEffect(() => { load(); }, [load]);

  const openEdit = (ticket) => {
    setEditingId(ticket._id);
    setStatusForm({ status: ticket.status, adminNote: ticket.adminNote || '' });
  };

  const handleUpdateStatus = async (ticketId) => {
    if (!statusForm.status) { toast.error('Select a status'); return; }
    setUpdatingId(ticketId);
    try {
      await updateTicketStatus(ticketId, statusForm);
      toast.success('Ticket status updated');
      setEditingId(null);
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setUpdatingId(null); }
  };

  const counts = {
    all:         tickets.length,
    Pending:     tickets.filter(t => t.status === 'Pending').length,
    'In Progress': tickets.filter(t => t.status === 'In Progress').length,
    Resolved:    tickets.filter(t => t.status === 'Resolved').length,
    Closed:      tickets.filter(t => t.status === 'Closed').length,
  };

  return (
    <div className="animate-in">
      <div className="page-hd">
        <div className="page-hd-text">
          <h1>Support Tickets</h1>
          <p>{tickets.length} ticket{tickets.length !== 1 ? 's' : ''}{filterStatus ? ` — ${filterStatus}` : ''}</p>
        </div>
        <Button variant="ghost" size="sm" icon={RefreshCw} onClick={load} />
      </div>

      <div style={{ display:'flex', gap:'0.4rem', marginBottom:'1.25rem', flexWrap:'wrap' }}>
        {[['', 'All'], ...TICKET_STATUSES.map(s => [s, s])].map(([val, label]) => (
          <button key={val} onClick={() => setFilterStatus(val)}
            style={{
              padding:'0.35rem 0.9rem', borderRadius:20, fontSize:'0.78rem', fontWeight:600,
              cursor:'pointer', border:'1px solid',
              background: filterStatus === val ? 'var(--gold-glow)' : 'var(--bg-raised)',
              borderColor: filterStatus === val ? 'var(--gold)' : 'var(--border-subtle)',
              color: filterStatus === val ? 'var(--gold)' : 'var(--text-muted)',
              transition:'all var(--t-fast)',
            }}>
            {label}
            {counts[val || 'all'] > 0 && (
              <span style={{ marginLeft:'0.35rem', background:'var(--bg-overlay)', borderRadius:10, padding:'0 5px', fontSize:'0.7rem' }}>
                {counts[val || 'all']}
              </span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="loading-center"><div className="spinner" /></div>
      ) : tickets.length === 0 ? (
        <div className="empty-state card">
          <TicketCheck size={36} strokeWidth={1} />
          <h3>No tickets</h3>
          <p>{filterStatus ? `No ${filterStatus} tickets` : 'No support tickets yet'}</p>
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:'0.65rem' }}>
          {tickets.map(t => (
            <div key={t._id} style={{ background:'var(--bg-surface)', border:'1px solid var(--border-subtle)', borderRadius:'var(--radius)', overflow:'hidden' }}>
              <div style={{ display:'flex', alignItems:'center', gap:'1rem', padding:'1rem 1.25rem', cursor:'pointer' }}
                onClick={() => setExpandedId(expandedId === t._id ? null : t._id)}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:'0.65rem', flexWrap:'wrap' }}>
                    <span style={{ fontFamily:'monospace', fontSize:'0.75rem', color:'var(--gold)', fontWeight:700 }}>{t.ticketNumber}</span>
                    <span style={{ fontWeight:600, fontSize:'0.875rem', color:'var(--text)' }}>{t.subject}</span>
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:'0.75rem', marginTop:'0.3rem', flexWrap:'wrap' }}>
                    <span style={{ fontSize:'0.78rem', color:'var(--text-muted)' }}>
                      {t.userName} <span className="uid-tag" style={{ marginLeft:'0.25rem' }}>{t.userUid}</span>
                    </span>
                    <span style={{ fontSize:'0.75rem', color:'var(--text-dim)' }}>
                      {new Date(t.createdAt).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })}
                    </span>
                  </div>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:'0.6rem', flexShrink:0 }}>
                  <span className={`badge ${ticketStatusBadge(t.status)}`}>{t.status}</span>
                  <ChevronDown size={14} color="var(--text-dim)"
                    style={{ transform: expandedId === t._id ? 'rotate(180deg)' : 'none', transition:'transform 0.2s' }} />
                </div>
              </div>

              {expandedId === t._id && (
                <div style={{ borderTop:'1px solid var(--border-subtle)', padding:'1.25rem' }}>
                  <div style={{ fontSize:'0.85rem', color:'var(--text-secondary)', lineHeight:1.7, marginBottom:'1rem', whiteSpace:'pre-wrap' }}>
                    {t.description}
                  </div>
                  {t.attachment && (
                    <div style={{ marginBottom:'1rem' }}>
                      <a href={t.attachment} download="attachment" target="_blank" rel="noreferrer"
                        style={{ fontSize:'0.8rem', color:'var(--gold)', display:'inline-flex', alignItems:'center', gap:'0.3rem' }}>
                        <FileText size={13} /> View Attachment
                      </a>
                    </div>
                  )}
                  {t.statusHistory?.length > 0 && (
                    <div style={{ marginBottom:'1rem' }}>
                      <div style={{ fontSize:'0.7rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.05em', color:'var(--text-dim)', marginBottom:'0.4rem' }}>History</div>
                      <div style={{ display:'flex', flexDirection:'column', gap:'0.25rem' }}>
                        {t.statusHistory.map((h, i) => (
                          <div key={i} style={{ display:'flex', alignItems:'center', gap:'0.5rem', fontSize:'0.75rem', color:'var(--text-muted)' }}>
                            <span className={`badge ${ticketStatusBadge(h.status)}`} style={{ fontSize:'0.65rem' }}>{h.status}</span>
                            {h.note && <span>{h.note}</span>}
                            <span style={{ marginLeft:'auto', color:'var(--text-dim)', whiteSpace:'nowrap' }}>{new Date(h.changedAt).toLocaleDateString('en-IN')}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {editingId === t._id ? (
                    <div style={{ background:'var(--bg-raised)', border:'1px solid var(--border-subtle)', borderRadius:'var(--radius-sm)', padding:'1rem', display:'flex', flexDirection:'column', gap:'0.75rem' }}>
                      <div style={{ fontWeight:600, fontSize:'0.82rem', color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.05em' }}>Update Status</div>
                      <div style={{ display:'flex', gap:'0.4rem', flexWrap:'wrap' }}>
                        {TICKET_STATUSES.map(s => (
                          <button key={s} onClick={() => setStatusForm(f => ({ ...f, status: s }))}
                            style={{
                              padding:'0.3rem 0.85rem', borderRadius:20, fontSize:'0.78rem', fontWeight:600,
                              cursor:'pointer', border:'1px solid',
                              background: statusForm.status === s ? 'var(--gold-glow)' : 'var(--bg-overlay)',
                              borderColor: statusForm.status === s ? 'var(--gold)' : 'var(--border-subtle)',
                              color: statusForm.status === s ? 'var(--gold)' : 'var(--text-muted)',
                            }}>
                            {s}
                          </button>
                        ))}
                      </div>
                      <div className="field" style={{ margin:0 }}>
                        <label className="field-label">Response / Note to User</label>
                        <div className="field-control">
                          <textarea className="field-input" rows={3}
                            placeholder="Optional note visible to the user…"
                            value={statusForm.adminNote}
                            onChange={e => setStatusForm(f => ({ ...f, adminNote: e.target.value }))}
                            style={{ resize:'vertical' }} />
                        </div>
                      </div>
                      <div style={{ display:'flex', gap:'0.5rem', justifyContent:'flex-end' }}>
                        <Button variant="subtle" size="sm" onClick={() => setEditingId(null)}>Cancel</Button>
                        <Button size="sm" icon={Check} loading={updatingId === t._id} onClick={() => handleUpdateStatus(t._id)}>
                          Update Status
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display:'flex', justifyContent:'flex-end' }}>
                      <Button size="sm" variant="ghost" icon={Pencil} onClick={() => openEdit(t)}>
                        Update Status
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Main Admin Component ──────────────────────────────────────────────────────
const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user: adminUser, logout, startImpersonation, isImpersonating } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();

  const [tab,            setTab]            = useState('dashboard');
  const [stats,          setStats]          = useState(null);
  const [users,          setUsers]          = useState([]);
  const [billing,        setBilling]        = useState([]);
  const [societyMap,     setSocietyMap]     = useState([]);
  const [companySettings,setCompanySettings]= useState(null);
  const [loadingUsers,   setLoadingUsers]   = useState(false);
  const [userFilter,     setUserFilter]     = useState(null);

  const [createModal,   setCreateModal]   = useState(false);
  const [settingsModal, setSettingsModal] = useState({ open: false, user: null });
  const [controlModal,  setControlModal]  = useState({ open: false, user: null });
  const [resetModal,    setResetModal]    = useState({ open: false, user: null });
  const [billingModal,  setBillingModal]  = useState({ open: false, user: null });
  const [editModal,     setEditModal]     = useState({ open: false, user: null });
  const [editUserForm,  setEditUserForm]  = useState({ name: '', email: '', phone: '' });
  const [logoModal,     setLogoModal]     = useState(false);
  const [formLoading,   setFormLoading]   = useState(false);

  const [createForm, setCreateForm] = useState({
    userId: '', password: '', name: '', email: '', phone: '',
    societyLimit: 5, assignedModules: [], moduleAssignmentDates: {},
  });
  const [settingsForm, setSettingsForm] = useState({ societyName: '', financialYear: '' });
  const [controlForm,  setControlForm]  = useState({ assignedModules: [], societyLimit: 5, moduleAssignmentDates: {} });
  const [resetForm,    setResetForm]    = useState({ newPassword: '', showNew: false });
  const [logoPreview,  setLogoPreview]  = useState(getLogoUrl() || '');

  const fetchStats = useCallback(async () => {
    try { const r = await getDashboardStats(); setStats(r.data.stats); } catch { toast.error('Failed to load stats'); }
  }, []);

  const fetchUsers = useCallback(async () => {
    setLoadingUsers(true);
    try { const r = await getAllUsers(); setUsers(r.data.users); } catch { toast.error('Failed to load users'); }
    finally { setLoadingUsers(false); }
  }, []);

  const fetchBilling = useCallback(async () => {
    try { const r = await getAllBilling(); setBilling(r.data.billing || []); } catch { toast.error('Failed to load billing'); }
  }, []);

  const fetchSocietyMap = useCallback(async () => {
    try {
      const m = await import('../../api/client');
      const r = await m.default.get('/admin/all-societies');
      setSocietyMap(r.data.map || []);
    } catch { toast.error('Failed to load societies'); }
  }, []);

  useEffect(() => {
    fetchStats(); fetchUsers();
    import('../../api/client').then(m => m.default.get('/company-settings'))
      .then(r => setCompanySettings(r.data.settings)).catch(() => {});
  }, [fetchStats, fetchUsers]);

  useEffect(() => {
    if (tab === 'billing')   { fetchBilling(); if (users.length === 0) fetchUsers(); }
    if (tab === 'societies') fetchSocietyMap();
  }, [tab, fetchBilling, fetchSocietyMap]);

  const handleCreate = async (e) => {
    e.preventDefault(); setFormLoading(true);
    try {
      await createUser(createForm); toast.success('User created'); setCreateModal(false);
      setCreateForm({ userId:'', password:'', name:'', email:'', phone:'', societyLimit:5, assignedModules:[], moduleAssignmentDates:{} });
      fetchUsers(); fetchStats();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setFormLoading(false); }
  };

  const handleEditUser = async (e) => {
    e.preventDefault(); setFormLoading(true);
    try {
      await updateUser(editModal.user._id, editUserForm); toast.success('User updated');
      setEditModal({ open:false, user:null }); fetchUsers();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setFormLoading(false); }
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault(); setFormLoading(true);
    try {
      await updateUserSettings(settingsModal.user._id, settingsForm); toast.success('Settings updated');
      setSettingsModal({ open:false, user:null }); fetchUsers();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setFormLoading(false); }
  };

  const handleSaveControl = async (e) => {
    e.preventDefault(); setFormLoading(true);
    try {
      await updateUserControl(controlModal.user._id, controlForm); toast.success('User control updated');
      setControlModal({ open:false, user:null }); fetchUsers();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setFormLoading(false); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault(); setFormLoading(true);
    try {
      await resetUserPassword(resetModal.user._id, { newPassword: resetForm.newPassword });
      toast.success('Password reset'); setResetModal({ open:false, user:null });
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setFormLoading(false); }
  };

  const handleToggle = async (user) => {
    if (!window.confirm(`${user.isActive ? 'Deactivate' : 'Activate'} "${user.name}"?`)) return;
    try { await toggleUserActive(user._id); toast.success(`User ${user.isActive ? 'deactivated' : 'activated'}`); fetchUsers(); fetchStats(); }
    catch { toast.error('Failed'); }
  };

  const handleImpersonate = async (targetUser) => {
    if (!window.confirm(`Impersonate "${targetUser.name}"?`)) return;
    try {
      const res = await impersonate(targetUser._id);
      startImpersonation(res.data); toast.success(`Impersonating ${targetUser.name}`); navigate('/dashboard');
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const handleGenerateInvoice = async (targetUser) => {
    setFormLoading(true);
    try {
      const now = new Date();
      const from = new Date(now.getFullYear(), now.getMonth(), 1);
      const to   = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      await generateInvoice({ userId: targetUser._id, billingPeriod: { from, to } });
      toast.success('Invoice generated'); setBillingModal({ open:false, user:null });
      setTab('billing'); setTimeout(fetchBilling, 300);
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setFormLoading(false); }
  };

  const toggleModuleInForm = (key, form, setForm) => {
    setForm(f => {
      const has = f.assignedModules.includes(key);
      const modules = has ? f.assignedModules.filter(m => m !== key) : [...f.assignedModules, key];
      const dates = { ...f.moduleAssignmentDates };
      if (!has) dates[key] = new Date().toISOString().slice(0, 10);
      else delete dates[key];
      return { ...f, assignedModules: modules, moduleAssignmentDates: dates };
    });
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm('Delete this invoice?')) return;
    try {
      const c = await import('../../api/client').then(m => m.default);
      await c.delete(`/billing/${invoiceId}`); toast.success('Invoice deleted'); fetchBilling();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const handleLogoSave = () => { setLogoUrl(logoPreview); toast.success('Logo updated'); setLogoModal(false); window.location.reload(); };
  const handleLogoFile = (e) => { const file = e.target.files[0]; if (!file) return; const reader = new FileReader(); reader.onload = ev => setLogoPreview(ev.target.result); reader.readAsDataURL(file); };

  return (
    <div className="admin-layout">
      {isImpersonating && (
        <div className="nav-impersonate-bar">
          <span>Viewing as impersonated user</span>
          <Button size="sm" variant="ghost" icon={X} onClick={() => { logout(); navigate('/login'); }}>Exit</Button>
        </div>
      )}

      <header className="admin-nav">
        <div className="nav-left"><Logo size="sm" /><span className="nav-admin-badge">Admin</span></div>
        <nav className="nav-center">
          <div className="nav-tab-group">
            {TABS.map(({ key, label, Icon }) => (
              <button key={key} className={`nav-tab ${tab === key ? 'active' : ''}`} onClick={() => setTab(key)}>
                <Icon /> {label}
              </button>
            ))}
          </div>
        </nav>
        <div className="nav-right">
          <div className="nav-avatar">{adminUser?.name?.[0] ?? 'A'}</div>
          <button onClick={toggleTheme} style={{ background:'var(--bg-raised)', border:'1px solid var(--border-subtle)', borderRadius:'var(--radius-sm)', width:32, height:32, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', color:'var(--text-muted)' }}>
            {theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
          </button>
          <Button variant="subtle" size="sm" icon={LogOut} onClick={() => { logout(); navigate('/login'); }} />
        </div>
      </header>

      <main className="admin-main">

        {/* DASHBOARD */}
        {tab === 'dashboard' && (
          <div className="animate-in">
            <div className="page-hd">
              <div className="page-hd-text"><h1>Dashboard</h1><p>Welcome back, {adminUser?.name}</p></div>
              <div className="page-hd-actions">
                <Button variant="ghost" size="sm" icon={RefreshCw} onClick={fetchStats}>Refresh</Button>
                <Button size="sm" icon={Plus} onClick={() => setCreateModal(true)}>Create User</Button>
              </div>
            </div>
            <div className="stats-grid">
              <StatCard label="Total Users"     value={stats?.totalUsers}     icon={Users}     color="gold"    onClick={() => setUserFilter('all')}      ctaLabel="View all users" />
              <StatCard label="Active Users"    value={stats?.activeUsers}    icon={UserCheck} color="success" onClick={() => setUserFilter('active')}   ctaLabel="View active" />
              <StatCard label="Inactive Users"  value={stats?.inactiveUsers}  icon={UserX}     color="danger"  onClick={() => setUserFilter('inactive')} ctaLabel="View inactive" />
              <StatCard label="Total Societies" value={stats?.totalSocieties} icon={Building2} color="info"    onClick={() => setTab('societies')}       ctaLabel="View all societies" />
            </div>
            <div className="section-card">
              <div className="section-card-head">
                <h3>Recent Users</h3>
                <Button variant="flat" size="sm" iconRight={ChevronRight} onClick={() => setTab('users')}>View all</Button>
              </div>
              <table className="data-table">
                <thead><tr><th>User ID</th><th>Name</th><th>Modules</th><th>Status</th></tr></thead>
                <tbody>
                  {users.slice(0, 5).map(u => (
                    <tr key={u._id} className={!u.isActive ? 'row-inactive' : ''}>
                      <td><span className="uid-tag">{u.userId}</span></td>
                      <td><div className="user-cell"><div className="user-av">{u.name[0]}</div><span className="user-cell-name">{u.name}</span></div></td>
                      <td><div className="modules-chips">{u.assignedModules?.map(m => <span key={m} className="badge badge-gold">{m === 'year_end_accounting' ? 'YEA' : m === 'income_expenditure' ? 'I&E' : 'SB'}</span>)}</div></td>
                      <td><span className={`badge ${u.isActive ? 'badge-success' : 'badge-danger'}`}>{u.isActive ? 'Active' : 'Inactive'}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* USERS */}
        {tab === 'users' && (
          <div className="animate-in">
            <div className="page-hd">
              <div className="page-hd-text"><h1>Users</h1><p>{users.length} total users</p></div>
              <div className="page-hd-actions">
                <Button variant="ghost" size="sm" icon={RefreshCw} onClick={fetchUsers} />
                <Button size="sm" icon={Plus} onClick={() => setCreateModal(true)}>Create User</Button>
              </div>
            </div>
            {loadingUsers ? <div className="loading-center"><div className="spinner" /></div> : (
              <div className="section-card">
                <table className="data-table">
                  <thead><tr><th>User ID</th><th>Name</th><th>Status</th><th>Societies</th><th>Modules</th><th>Actions</th></tr></thead>
                  <tbody>
                    {users.map(u => (
                      <tr key={u._id} className={!u.isActive ? 'row-inactive' : ''}>
                        <td><span className="uid-tag">{u.userId}</span></td>
                        <td><div className="user-cell"><div className="user-av">{u.name[0]}</div><div><div className="user-cell-name">{u.name}</div><div className="user-cell-email">{u.email || '—'}</div></div></div></td>
                        <td><span className={`badge ${u.isActive ? 'badge-success' : 'badge-danger'}`}>{u.isActive ? 'Active' : 'Inactive'}</span></td>
                        <td style={{ color:'var(--text)' }}>{u.societyLimit}</td>
                        <td><div className="modules-chips">{u.assignedModules?.length ? u.assignedModules.map(m => <span key={m} className="badge badge-gold">{m === 'year_end_accounting' ? 'YEA' : m === 'income_expenditure' ? 'I&E' : 'SB'}</span>) : <span className="text-xs text-muted">None</span>}</div></td>
                        <td>
                          <div className="action-row">
                            <Button size="xs" variant="ghost" icon={Pencil} onClick={() => { setEditUserForm({ name:u.name||'', email:u.email||'', phone:u.phone||'' }); setEditModal({ open:true, user:u }); }} title="Edit" />
                            <Button size="xs" variant="ghost" icon={SlidersHorizontal} onClick={() => { setControlForm({ assignedModules:u.assignedModules||[], societyLimit:u.societyLimit||5, moduleAssignmentDates:u.moduleAssignmentDates||{} }); setControlModal({ open:true, user:u }); }} title="Control" />
                            <Button size="xs" variant="ghost" icon={KeyRound} onClick={() => setResetModal({ open:true, user:u })} title="Reset password" />
                            <Button size="xs" variant="ghost" icon={Receipt}  onClick={() => setBillingModal({ open:true, user:u })} title="Billing" />
                            <Button size="xs" variant={u.isActive ? 'danger' : 'success'} icon={u.isActive ? ToggleLeft : ToggleRight} onClick={() => handleToggle(u)}>{u.isActive ? 'Deactivate' : 'Activate'}</Button>
                            <Button size="xs" variant="subtle" icon={Eye} onClick={() => handleImpersonate(u)}>Impersonate</Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* BILLING */}
        {tab === 'billing' && (
          <div className="animate-in">
            <div className="page-hd"><div className="page-hd-text"><h1>Billing</h1></div></div>
            <div className="pricing-grid mb-3">
              <div className="pricing-card"><div className="pricing-card-amount">₹500</div><div className="pricing-card-label">Year End Accounting</div><div className="pricing-card-unit">per society / year</div></div>
              <div className="pricing-card"><div className="pricing-card-amount">₹20</div><div className="pricing-card-label">Society Book</div><div className="pricing-card-unit">per member / month</div></div>
            </div>
            <div className="section-card">
              <div className="section-card-head"><h3>Invoices</h3></div>
              {billing.length === 0
                ? <div className="empty-state"><Receipt size={36}/><h3>No invoices yet</h3></div>
                : (
                  <table className="data-table">
                    <thead><tr><th>Invoice #</th><th>User</th><th>Amount</th><th>Status</th><th>Date</th><th>Action</th></tr></thead>
                    <tbody>
                      {billing.map(b => (
                        <tr key={b._id}>
                          <td style={{ color:'var(--gold)', fontWeight:600 }}>{b.invoiceNumber}</td>
                          <td>{b.userId?.name || '—'}</td>
                          <td style={{ fontWeight:600 }}>₹{b.totalAmount}</td>
                          <td><span className={`badge ${b.status==='paid'?'badge-success':b.status==='overdue'?'badge-danger':'badge-warning'}`}>{b.status}</span></td>
                          <td className="text-sm text-muted">{new Date(b.createdAt).toLocaleDateString('en-IN')}</td>
                          <td>
                            <div style={{ display:'flex', gap:'0.35rem', flexWrap:'wrap' }}>
                              {b.status !== 'paid' && <Button size="xs" variant="success" icon={Check} onClick={() => updateInvoiceStatus(b._id,'paid').then(fetchBilling)}>Mark Paid</Button>}
                              <Button size="xs" variant="ghost" icon={FileText} onClick={() => generateInvoicePDF(b, companySettings||{}, 'proforma')}>Proforma</Button>
                              {b.status === 'paid' && <Button size="xs" variant="primary" icon={Receipt} onClick={() => generateInvoicePDF(b, companySettings||{}, 'tax')}>Tax Invoice</Button>}
                              {b.status !== 'paid' && <Button size="xs" variant="danger" icon={Trash2} onClick={() => handleDeleteInvoice(b._id)} />}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )
              }
            </div>
          </div>
        )}

        {/* SOCIETIES */}
        {tab === 'societies' && <SocietiesTab societyMap={societyMap} onRefresh={fetchSocietyMap} stats={stats} />}

        {/* TICKETS */}
        {tab === 'tickets' && <TicketsTab />}

        {/* SETTINGS */}
        {tab === 'settings' && (
          <div className="animate-in">
            <div className="page-hd"><div className="page-hd-text"><h1>Settings</h1><p>Platform & company configuration</p></div></div>
            <div className="section-card" style={{ maxWidth:560, marginBottom:'1.5rem' }}>
              <div className="section-card-head"><h3>Logo Management</h3></div>
              <div style={{ padding:'1.5rem' }}>
                <p className="text-sm text-muted mb-2">Upload your company logo.</p>
                {getLogoUrl() && <img src={getLogoUrl()} alt="Current logo" className="logo-preview" />}
                <Button icon={Upload} onClick={() => setLogoModal(true)}>Upload / Change Logo</Button>
              </div>
            </div>
            <CompanySettings />
          </div>
        )}
      </main>

      {userFilter && <UserFilterPanel users={users} filter={userFilter} onClose={() => setUserFilter(null)} />}

      {/* MODALS */}
      <Modal isOpen={createModal} onClose={() => setCreateModal(false)} title="Create User" subtitle="Add a new user to the platform" size="lg"
        footer={<><Button variant="subtle" onClick={() => setCreateModal(false)}>Cancel</Button><Button loading={formLoading} icon={Plus} onClick={handleCreate}>Create User</Button></>}>
        <form onSubmit={handleCreate} className="mform">
          <div className="mform-grid">
            <Input label="User ID"   value={createForm.userId}   onChange={e=>setCreateForm(f=>({...f,userId:e.target.value}))}   placeholder="e.g. USR001" required />
            <Input label="Password"  type="password" value={createForm.password} onChange={e=>setCreateForm(f=>({...f,password:e.target.value}))} placeholder="Min 6 characters" required />
          </div>
          <Input label="Full Name" value={createForm.name} onChange={e=>setCreateForm(f=>({...f,name:e.target.value}))} placeholder="Full name" required />
          <div className="mform-grid">
            <Input label="Email" type="email" value={createForm.email} onChange={e=>setCreateForm(f=>({...f,email:e.target.value}))} placeholder="user@email.com" />
            <Input label="Phone" value={createForm.phone} onChange={e=>setCreateForm(f=>({...f,phone:e.target.value}))} placeholder="+91 98765 43210" />
          </div>
          <div className="mform-grid"><Input label="Society Limit" type="number" min="1" value={createForm.societyLimit} onChange={e=>setCreateForm(f=>({...f,societyLimit:+e.target.value}))} /><div /></div>
          <div className="mform-section">
            <div className="mform-section-title">Module Assignment</div>
            {MODULES.map(m => { const sel = createForm.assignedModules.includes(m.key); return (<div key={m.key} className={`module-toggle ${sel?'selected':''}`} onClick={() => toggleModuleInForm(m.key, createForm, setCreateForm)}><div className="module-toggle-info"><strong>{m.label}</strong><span>{m.price}</span></div><div className="module-toggle-check">{sel && <Check size={12} />}</div></div>); })}
            {createForm.assignedModules.map(key => (<Input key={key} label={`${MODULES.find(m=>m.key===key)?.label} — Assignment Date`} type="date" value={createForm.moduleAssignmentDates[key]||''} onChange={e=>setCreateForm(f=>({...f,moduleAssignmentDates:{...f.moduleAssignmentDates,[key]:e.target.value}}))} />))}
          </div>
        </form>
      </Modal>

      <Modal isOpen={editModal.open} onClose={()=>setEditModal({open:false,user:null})} title="Edit User" subtitle={editModal.user?.name} size="sm"
        footer={<><Button variant="subtle" onClick={()=>setEditModal({open:false,user:null})}>Cancel</Button><Button loading={formLoading} icon={Check} onClick={handleEditUser}>Save</Button></>}>
        <form onSubmit={handleEditUser} className="mform">
          <Input label="Full Name" value={editUserForm.name} onChange={e=>setEditUserForm(f=>({...f,name:e.target.value}))} placeholder="Full name" required />
          <Input label="Email" type="email" value={editUserForm.email} onChange={e=>setEditUserForm(f=>({...f,email:e.target.value}))} placeholder="user@email.com" />
          <Input label="Phone" value={editUserForm.phone} onChange={e=>setEditUserForm(f=>({...f,phone:e.target.value}))} placeholder="+91 98765 43210" />
        </form>
      </Modal>

      <Modal isOpen={settingsModal.open} onClose={()=>setSettingsModal({open:false,user:null})} title="User Settings" subtitle={settingsModal.user?.name} size="sm"
        footer={<><Button variant="subtle" onClick={()=>setSettingsModal({open:false,user:null})}>Cancel</Button><Button loading={formLoading} onClick={handleSaveSettings}>Save</Button></>}>
        <form onSubmit={handleSaveSettings} className="mform">
          <Input label="Society Name" value={settingsForm.societyName} onChange={e=>setSettingsForm(f=>({...f,societyName:e.target.value}))} placeholder="e.g. Sunshine Apartments" />
          <div className="field"><label className="field-label">Financial Year</label><div className="field-control"><select className="field-input" value={settingsForm.financialYear} onChange={e=>setSettingsForm(f=>({...f,financialYear:e.target.value}))}><option value="">-- Select FY --</option>{(()=>{const now=new Date();const cy=now.getMonth()>=3?now.getFullYear():now.getFullYear()-1;const opts=[];for(let y=cy-8;y<=cy+7;y++){const label=`${y}-${String(y+1).slice(2)}`;opts.push(<option key={label} value={label}>{label}</option>);}return opts;})()}</select></div></div>
        </form>
      </Modal>

      <Modal isOpen={controlModal.open} onClose={()=>setControlModal({open:false,user:null})} title="User Control" subtitle={controlModal.user?.name} size="md"
        footer={<><Button variant="subtle" onClick={()=>setControlModal({open:false,user:null})}>Cancel</Button><Button loading={formLoading} onClick={handleSaveControl}>Save Control</Button></>}>
        <form onSubmit={handleSaveControl} className="mform">
          <Input label="Society Limit" type="number" min="1" value={controlForm.societyLimit} onChange={e=>setControlForm(f=>({...f,societyLimit:+e.target.value}))} />
          <div className="mform-section">
            <div className="mform-section-title">Module Assignment</div>
            {MODULES.map(m => { const sel = controlForm.assignedModules.includes(m.key); return (<div key={m.key} className={`module-toggle ${sel?'selected':''}`} onClick={() => toggleModuleInForm(m.key, controlForm, setControlForm)}><div className="module-toggle-info"><strong>{m.label}</strong><span>{m.price}</span></div><div className="module-toggle-check">{sel && <Check size={12} />}</div></div>); })}
            {controlForm.assignedModules.map(key => (<Input key={key} label={`${MODULES.find(m=>m.key===key)?.label} — Assignment Date`} type="date" value={controlForm.moduleAssignmentDates[key]||''} onChange={e=>setControlForm(f=>({...f,moduleAssignmentDates:{...f.moduleAssignmentDates,[key]:e.target.value}}))} />))}
          </div>
        </form>
      </Modal>

      <Modal isOpen={resetModal.open} onClose={()=>setResetModal({open:false,user:null})} title="Reset Password" subtitle={resetModal.user?.name} size="sm"
        footer={<><Button variant="subtle" onClick={()=>setResetModal({open:false,user:null})}>Cancel</Button><Button variant="danger" loading={formLoading} onClick={handleResetPassword}>Reset Password</Button></>}>
        <form onSubmit={handleResetPassword} className="mform">
          <Input label="New Password" type={resetForm.showNew?'text':'password'} value={resetForm.newPassword} onChange={e=>setResetForm(f=>({...f,newPassword:e.target.value}))} iconLeft={KeyRound} iconRight={resetForm.showNew?EyeOff:Eye} onIconRightClick={()=>setResetForm(f=>({...f,showNew:!f.showNew}))} placeholder="Min 6 characters" required />
        </form>
      </Modal>

      <Modal isOpen={billingModal.open} onClose={()=>setBillingModal({open:false,user:null})} title="Generate Invoice" subtitle={billingModal.user?.name} size="sm"
        footer={<><Button variant="subtle" onClick={()=>setBillingModal({open:false,user:null})}>Cancel</Button><Button loading={formLoading} icon={Receipt} onClick={()=>handleGenerateInvoice(billingModal.user)}>Generate Invoice</Button></>}>
        <p className="text-sm text-secondary">This will calculate charges for the current month based on {billingModal.user?.name}'s active societies and assigned modules.</p>
      </Modal>

      <Modal isOpen={logoModal} onClose={()=>setLogoModal(false)} title="Upload Logo" subtitle="This logo will appear across all pages" size="sm"
        footer={<><Button variant="subtle" onClick={()=>setLogoModal(false)}>Cancel</Button><Button icon={Check} onClick={handleLogoSave}>Save Logo</Button></>}>
        <div className="mform">
          <label className="logo-upload-area">
            <input type="file" accept="image/*" style={{ display:'none' }} onChange={handleLogoFile} />
            {logoPreview ? <img src={logoPreview} alt="Preview" className="logo-preview" /> : <div style={{ color:'var(--text-muted)', fontSize:'0.875rem' }}><Upload size={24} style={{ margin:'0 auto 0.5rem', display:'block' }} />Click to upload logo</div>}
          </label>
          {logoPreview && <Button variant="danger" size="sm" onClick={() => { setLogoPreview(''); setLogoUrl(''); }}>Remove Logo</Button>}
        </div>
      </Modal>
    </div>
  );
};

// ── Societies Tab ─────────────────────────────────────────────────────────────
const SocietiesTab = ({ societyMap, onRefresh }) => {
  const [expanded,    setExpanded]    = React.useState({});
  const [editSociety, setEditSociety] = React.useState(null);  // society being edited
  const [editTab,     setEditTab]     = React.useState('society'); // 'society' | 'fy'
  const [editForm,    setEditForm]    = React.useState({});
  const [saving,      setSaving]      = React.useState(false);

  // FY state
  const [fys,         setFys]         = React.useState([]);
  const [fysLoading,  setFysLoading]  = React.useState(false);
  const [fyForms,     setFyForms]     = React.useState({}); // keyed by fy._id
  const [fySaving,    setFySaving]    = React.useState({}); // keyed by fy._id

  const toggle = (id) => setExpanded(e => ({ ...e, [id]: !e[id] }));

  const openEdit = (s, e) => {
    e.stopPropagation();
    setEditSociety(s);
    setEditTab('society');
    setEditForm({
      name:               s.name || '',
      registrationNumber: s.registrationNumber || '',
      address: {
        line1:   s.address?.line1   || '',
        city:    s.address?.city    || '',
        state:   s.address?.state   || '',
        pincode: s.address?.pincode || '',
      },
    });
    setFys([]);
    setFyForms({});
  };

  // Load FYs when the FY tab is opened
  const loadFYs = React.useCallback(async (societyId) => {
    setFysLoading(true);
    try {
      const m = await import('../../api/client');
      const r = await m.default.get(`/admin/societies/${societyId}/financial-years`);
      const list = r.data.financialYears || [];
      setFys(list);
      // Initialise editable form state for each FY
      const forms = {};
      list.forEach(fy => {
        forms[fy._id] = {
          fyName:             fy.fyName,
          startDate:          fy.startDate ? fy.startDate.slice(0, 10) : '',
          endDate:            fy.endDate   ? fy.endDate.slice(0, 10)   : '',
          cashOpeningBalance: fy.cashOpeningBalance ?? 0,
          bankOpeningBalances: (fy.bankOpeningBalances || []).map(b => ({
            _id:            b._id,
            bankName:       b.bankName,
            openingBalance: b.openingBalance ?? 0,
          })),
        };
      });
      setFyForms(forms);
    } catch { toast.error('Failed to load financial years'); }
    finally { setFysLoading(false); }
  }, []);

  // When tab switches to 'fy', fetch if not yet loaded
  React.useEffect(() => {
    if (editTab === 'fy' && editSociety && fys.length === 0 && !fysLoading) {
      loadFYs(editSociety._id);
    }
  }, [editTab, editSociety, fys.length, fysLoading, loadFYs]);

  const handleSocietySave = async () => {
    if (!editForm.name) { toast.error('Society name is required'); return; }
    setSaving(true);
    try {
      const m = await import('../../api/client');
      await m.default.put(`/societies/${editSociety._id}`, editForm);
      toast.success('Society updated');
      setEditSociety(null);
      onRefresh();
    } catch { toast.error('Failed to update society'); }
    finally { setSaving(false); }
  };

  const handleFYSave = async (fyId) => {
    setFySaving(s => ({ ...s, [fyId]: true }));
    try {
      const m = await import('../../api/client');
      await m.default.put(`/admin/financial-years/${fyId}`, fyForms[fyId]);
      toast.success('Financial year updated');
    } catch { toast.error('Failed to update financial year'); }
    finally { setFySaving(s => ({ ...s, [fyId]: false })); }
  };

  const updateFYForm = (fyId, field, value) => {
    setFyForms(f => ({ ...f, [fyId]: { ...f[fyId], [field]: value } }));
  };

  const updateBankInFY = (fyId, bankIdx, field, value) => {
    setFyForms(f => {
      const banks = [...f[fyId].bankOpeningBalances];
      banks[bankIdx] = { ...banks[bankIdx], [field]: value };
      return { ...f, [fyId]: { ...f[fyId], bankOpeningBalances: banks } };
    });
  };

  const totalCount = societyMap.reduce((s, e) => s + e.societies.length, 0);

  // ── Tab pill style helper
  const tabStyle = (active) => ({
    padding: '0.4rem 1rem',
    fontSize: '0.82rem',
    fontWeight: 600,
    borderRadius: 20,
    cursor: 'pointer',
    border: '1px solid',
    background:   active ? 'var(--gold-glow)'    : 'var(--bg-overlay)',
    borderColor:  active ? 'var(--gold)'          : 'var(--border-subtle)',
    color:        active ? 'var(--gold)'           : 'var(--text-muted)',
    transition: 'all var(--t-fast)',
  });

  return (
    <div className="animate-in">
      <div className="page-hd">
        <div className="page-hd-text">
          <h1>Societies</h1>
          <p>{totalCount} total societies across {societyMap.filter(e => e.societies.length > 0).length} users</p>
        </div>
        <Button variant="ghost" size="sm" icon={RefreshCw} onClick={onRefresh} />
      </div>

      {societyMap.length === 0
        ? <div className="empty-state card"><Building2 size={40} strokeWidth={1} /><h3>No societies yet</h3></div>
        : (
          <div style={{ display:'flex', flexDirection:'column', gap:'0.75rem' }}>
            {societyMap.map(entry => {
              const isOpen = expanded[entry.user._id];
              const count  = entry.societies.length;
              return (
                <div key={entry.user._id} style={{ background:'var(--bg-surface)', border:'1px solid var(--border-subtle)', borderRadius:'var(--radius)', overflow:'hidden' }}>
                  <div onClick={() => toggle(entry.user._id)} style={{ display:'flex', alignItems:'center', gap:'1rem', padding:'1rem 1.25rem', cursor:'pointer' }}>
                    <div className="user-av">{entry.user.name[0]}</div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:600, color:'var(--text)', fontSize:'0.9rem' }}>{entry.user.name}</div>
                      <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', marginTop:'0.2rem' }}>
                        <span className="uid-tag">{entry.user.userId}</span>
                        <span className={`badge ${entry.user.isActive ? 'badge-success' : 'badge-danger'}`}>{entry.user.isActive ? 'Active' : 'Inactive'}</span>
                      </div>
                    </div>
                    <div style={{ display:'flex', alignItems:'center', gap:'0.75rem' }}>
                      <div style={{ textAlign:'right' }}>
                        <div style={{ fontFamily:'var(--font-display)', fontSize:'1.5rem', fontWeight:700, color:'var(--gold)', lineHeight:1 }}>{count}</div>
                        <div style={{ fontSize:'0.7rem', color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.05em' }}>Societ{count !== 1 ? 'ies' : 'y'}</div>
                      </div>
                      <ChevronRight size={16} color="var(--text-dim)" style={{ transform: isOpen ? 'rotate(90deg)' : 'none', transition:'transform 0.2s' }} />
                    </div>
                  </div>

                  {isOpen && count > 0 && (
                    <div style={{ borderTop:'1px solid var(--border-subtle)' }}>
                      <table className="data-table">
                        <thead>
                          <tr><th>Society Name</th><th>Reg. No</th><th>Members</th><th>Modules</th><th>City</th><th>Created</th><th>Action</th></tr>
                        </thead>
                        <tbody>
                          {entry.societies.map(s => (
                            <tr key={s._id}>
                              <td style={{ fontWeight:500, color:'var(--text)' }}>{s.name}</td>
                              <td style={{ fontSize:'0.8rem', color:'var(--text-muted)', fontFamily:'monospace' }}>{s.registrationNumber || '—'}</td>
                              <td><div style={{ display:'flex', alignItems:'center', gap:'0.35rem' }}><Users size={13} color="var(--text-muted)" /><span style={{ fontSize:'0.875rem' }}>{s.memberCount}</span></div></td>
                              <td><div style={{ display:'flex', gap:'0.3rem' }}>{s.activeModules?.length ? s.activeModules.map(m => <span key={m} className="badge badge-gold">{m === 'year_end_accounting' ? 'YEA' : m === 'income_expenditure' ? 'I&E' : 'SB'}</span>) : <span className="text-xs text-muted">None</span>}</div></td>
                              <td style={{ fontSize:'0.85rem', color:'var(--text-secondary)' }}>{s.address?.city || '—'}</td>
                              <td style={{ color:'var(--text-muted)', fontSize:'0.8rem' }}>{new Date(s.createdAt).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })}</td>
                              <td><Button size="xs" variant="ghost" icon={Pencil} onClick={(e) => openEdit(s, e)}>Edit</Button></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {isOpen && count === 0 && (
                    <div style={{ padding:'1rem 1.25rem', borderTop:'1px solid var(--border-subtle)', color:'var(--text-muted)', fontSize:'0.85rem' }}>No societies created yet</div>
                  )}
                </div>
              );
            })}
          </div>
        )
      }

      {/* ── Edit Modal ── */}
      {editSociety && (
        <div
          style={{ position:'fixed', inset:0, zIndex:1000, background:'rgba(7,14,26,0.75)', display:'flex', alignItems:'center', justifyContent:'center', padding:'1.5rem' }}
          onClick={() => setEditSociety(null)}
        >
          <div
            style={{ background:'var(--bg-surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', width:'100%', maxWidth:560, padding:'1.5rem', maxHeight:'90vh', overflowY:'auto' }}
            onClick={e => e.stopPropagation()}
          >
            {/* Modal header */}
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'1.25rem' }}>
              <h3 style={{ fontFamily:'var(--font-display)', margin:0 }}>{editSociety.name}</h3>
              <button onClick={() => setEditSociety(null)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', display:'flex', alignItems:'center' }}>
                <X size={18} />
              </button>
            </div>

            {/* Tab switcher */}
            <div style={{ display:'flex', gap:'0.4rem', marginBottom:'1.25rem' }}>
              <button style={tabStyle(editTab === 'society')} onClick={() => setEditTab('society')}>Society Details</button>
              <button style={tabStyle(editTab === 'fy')}      onClick={() => setEditTab('fy')}>Financial Years</button>
            </div>

            {/* ── Society Details tab ── */}
            {editTab === 'society' && (
              <div className="mform">
                <div className="field">
                  <label className="field-label">Society Name *</label>
                  <div className="field-control"><input type="text" className="field-input" value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} /></div>
                </div>
                <div className="field">
                  <label className="field-label">Registration Number</label>
                  <div className="field-control"><input type="text" className="field-input" value={editForm.registrationNumber} onChange={e => setEditForm(f => ({ ...f, registrationNumber: e.target.value }))} /></div>
                </div>
                <div className="field">
                  <label className="field-label">Address Line 1</label>
                  <div className="field-control"><input type="text" className="field-input" value={editForm.address.line1} onChange={e => setEditForm(f => ({ ...f, address: { ...f.address, line1: e.target.value } }))} /></div>
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.75rem' }}>
                  <div className="field">
                    <label className="field-label">City</label>
                    <div className="field-control"><input type="text" className="field-input" value={editForm.address.city} onChange={e => setEditForm(f => ({ ...f, address: { ...f.address, city: e.target.value } }))} /></div>
                  </div>
                  <div className="field">
                    <label className="field-label">State</label>
                    <div className="field-control"><input type="text" className="field-input" value={editForm.address.state} onChange={e => setEditForm(f => ({ ...f, address: { ...f.address, state: e.target.value } }))} /></div>
                  </div>
                </div>
                <div className="field">
                  <label className="field-label">Pincode</label>
                  <div className="field-control"><input type="text" className="field-input" value={editForm.address.pincode} onChange={e => setEditForm(f => ({ ...f, address: { ...f.address, pincode: e.target.value } }))} /></div>
                </div>
                <div style={{ display:'flex', justifyContent:'flex-end', gap:'0.75rem', marginTop:'0.5rem' }}>
                  <Button variant="subtle" onClick={() => setEditSociety(null)}>Cancel</Button>
                  <Button loading={saving} onClick={handleSocietySave}>Save Changes</Button>
                </div>
              </div>
            )}

            {/* ── Financial Years tab ── */}
            {editTab === 'fy' && (
              <div>
                {fysLoading ? (
                  <div className="loading-center"><div className="spinner" /></div>
                ) : fys.length === 0 ? (
                  <div style={{ textAlign:'center', padding:'2rem', color:'var(--text-muted)', fontSize:'0.875rem' }}>No financial years found for this society.</div>
                ) : (
                  <div style={{ display:'flex', flexDirection:'column', gap:'1rem' }}>
                    {fys.map(fy => {
                      const form = fyForms[fy._id];
                      if (!form) return null;
                      return (
                        <div key={fy._id} style={{ background:'var(--bg-raised)', border:'1px solid var(--border-subtle)', borderRadius:'var(--radius-sm)', padding:'1rem' }}>
                          {/* FY header */}
                          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'0.85rem' }}>
                            <span style={{ fontWeight:700, color:'var(--gold)', fontSize:'0.9rem' }}>{fy.fyName}</span>
                            <div style={{ display:'flex', gap:'0.4rem' }}>
                              {fy.isClosed && <span className="badge badge-subtle">Closed</span>}
                              {fy.isActive && !fy.isClosed && <span className="badge badge-success">Active</span>}
                            </div>
                          </div>

                          <div className="mform" style={{ gap:'0.75rem' }}>
                            {/* FY Name */}
                            <div className="field" style={{ margin:0 }}>
                              <label className="field-label">FY Name</label>
                              <div className="field-control">
                                <input type="text" className="field-input" value={form.fyName}
                                  onChange={e => updateFYForm(fy._id, 'fyName', e.target.value)} />
                              </div>
                            </div>

                            {/* Date range */}
                            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.75rem' }}>
                              <div className="field" style={{ margin:0 }}>
                                <label className="field-label">Start Date</label>
                                <div className="field-control">
                                  <input type="date" className="field-input" value={form.startDate}
                                    onChange={e => updateFYForm(fy._id, 'startDate', e.target.value)} />
                                </div>
                              </div>
                              <div className="field" style={{ margin:0 }}>
                                <label className="field-label">End Date</label>
                                <div className="field-control">
                                  <input type="date" className="field-input" value={form.endDate}
                                    onChange={e => updateFYForm(fy._id, 'endDate', e.target.value)} />
                                </div>
                              </div>
                            </div>

                            {/* Cash opening balance */}
                            <div className="field" style={{ margin:0 }}>
                              <label className="field-label">Cash Opening Balance (₹)</label>
                              <div className="field-control">
                                <input type="number" className="field-input" value={form.cashOpeningBalance}
                                  onChange={e => updateFYForm(fy._id, 'cashOpeningBalance', e.target.value)} />
                              </div>
                            </div>

                            {/* Bank opening balances */}
                            {form.bankOpeningBalances.length > 0 && (
                              <div>
                                <div style={{ fontSize:'0.72rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em', color:'var(--text-dim)', marginBottom:'0.5rem' }}>
                                  Bank Opening Balances
                                </div>
                                <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem' }}>
                                  {form.bankOpeningBalances.map((bank, idx) => (
                                    <div key={bank._id} style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.5rem' }}>
                                      <div className="field" style={{ margin:0 }}>
                                        <label className="field-label">Bank Name</label>
                                        <div className="field-control">
                                          <input type="text" className="field-input" value={bank.bankName}
                                            onChange={e => updateBankInFY(fy._id, idx, 'bankName', e.target.value)} />
                                        </div>
                                      </div>
                                      <div className="field" style={{ margin:0 }}>
                                        <label className="field-label">Opening Balance (₹)</label>
                                        <div className="field-control">
                                          <input type="number" className="field-input" value={bank.openingBalance}
                                            onChange={e => updateBankInFY(fy._id, idx, 'openingBalance', e.target.value)} />
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            <div style={{ display:'flex', justifyContent:'flex-end' }}>
                              <Button size="sm" loading={fySaving[fy._id]} icon={Check} onClick={() => handleFYSave(fy._id)}>
                                Save FY
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
